export const RESPONSE_SCALE = [
  { value: "independently_demonstrated", label: "Independently demonstrated" },
  { value: "minimal_prompting", label: "With minimal prompting" },
  { value: "with_support", label: "With support" },
  { value: "not_yet_demonstrated", label: "Not yet demonstrated" },
  { value: "unable_to_assess", label: "Unable to assess" },
  { value: "not_applicable", label: "Not applicable" },
];

export const RECOMMENDATIONS = [
  { value: "suitable", label: "Suitable for Independent Travel Training" },
  { value: "suitable_with_support", label: "Suitable with Additional Support / Adjustments" },
  { value: "further_information_required", label: "Further Information Required Before Decision" },
  { value: "further_assessment_required", label: "Further Assessment Required" },
  { value: "not_currently_suitable", label: "Not Currently Suitable for Independent Travel Training" },
  { value: "other_manager_review", label: "Other — Manager Review Required" },
];

export const ASSESSMENT_STATUS_LABELS = {
  draft: "Draft",
  in_progress: "In Progress",
  awaiting_information: "Awaiting Information",
  completed_by_assessor: "Completed by Assessor",
  awaiting_manager_review: "Awaiting Manager Review",
  provisional: "Provisional — Assessment Week",
  approved: "Approved",
  returned_for_amendment: "Returned for Amendment",
  cancelled: "Cancelled",
};

export const SAFETY_CRITICAL = new Set([
  "c_pavement_boundary", "c_stop_before_crossing", "c_look_before_crossing", "c_listen_traffic",
  "c_traffic_direction", "c_safe_crossing_locations", "c_pedestrian_crossings", "c_pedestrian_signals",
  "c_zebra", "c_signal_controlled", "c_parked_vehicles", "c_driveways", "c_bicycles", "c_motorcycles",
  "c_electric_vehicles", "c_judge_help_needed", "c_attentive_near_roads", "c_avoid_distraction",
  "c_follow_crossing_strategy", "f_strangers", "f_not_leaving_with_unknown", "f_not_entering_vehicle",
  "f_inappropriate_requests", "f_feel_unsafe",
]);

const item = (key, label) => ({ key, label });

export const GROUPS = [
  { id: "details", num: 0, title: "Assessment Details", type: "details" },
  {
    id: "a", num: 1, title: "Journey Understanding", type: "scale",
    intro: "Section A — the child's understanding of the journey.",
    items: [
      item("a_why_training", "Understands why travel training is being considered"),
      item("a_where_from", "Can describe where they travel from"),
      item("a_identify_school", "Can identify their school/destination"),
      item("a_journey_purpose", "Can explain the general purpose of the journey"),
      item("a_landmarks", "Can recognise important landmarks"),
      item("a_recognise_destination", "Can recognise their destination"),
      item("a_direction", "Can identify the correct direction of travel"),
      item("a_correct_stop", "Can recognise when they have reached the correct stop/station"),
      item("a_follow_instructions", "Can follow simple journey instructions"),
      item("a_sequence", "Can remember a sequence of travel steps"),
      item("a_something_wrong", "Can recognise when something about the journey is wrong or unexpected"),
    ],
  },
  {
    id: "b", num: 2, title: "Communication", type: "scale",
    intro: "Section B — communication skills relevant to safe travel.",
    items: [
      item("b_state_name", "Can state or communicate their name"),
      item("b_where_going", "Can communicate where they are going"),
      item("b_need_help", "Can communicate when they need help"),
      item("b_verbal_instructions", "Can understand basic verbal instructions"),
      item("b_signs_symbols", "Can understand relevant signs/symbols where applicable"),
      item("b_ask_help", "Can ask an appropriate person for help"),
      item("b_explain_lost", "Can explain that they are lost"),
      item("b_emergency", "Can communicate an emergency"),
      item("b_preferred_method", "Can use their preferred communication method effectively"),
      item("b_transport_staff", "Can communicate relevant needs to transport staff"),
      item("b_unfamiliar_person", "Responds appropriately when approached by an unfamiliar person"),
    ],
  },
  {
    id: "c", num: 3, title: "Road Safety", type: "scale",
    intro: "Section C — detailed road safety. Comments are required on safety-critical items where significant support is needed or the skill is not yet demonstrated.",
    concern: true,
    items: [
      item("c_pavement_boundary", "Recognises the pavement/road boundary"),
      item("c_stop_before_crossing", "Stops before crossing"),
      item("c_look_before_crossing", "Looks appropriately before crossing"),
      item("c_listen_traffic", "Listens for traffic"),
      item("c_traffic_direction", "Understands traffic direction"),
      item("c_safe_crossing_locations", "Identifies safe crossing locations"),
      item("c_pedestrian_crossings", "Recognises pedestrian crossings"),
      item("c_pedestrian_signals", "Understands pedestrian traffic signals"),
      item("c_zebra", "Uses zebra crossings appropriately"),
      item("c_signal_controlled", "Uses pelican/puffin/signal-controlled crossings where applicable"),
      item("c_parked_vehicles", "Understands parked vehicle hazards"),
      item("c_driveways", "Awareness of driveways"),
      item("c_bicycles", "Awareness of bicycles"),
      item("c_motorcycles", "Awareness of motorcycles"),
      item("c_electric_vehicles", "Awareness of electric vehicles where relevant"),
      item("c_judge_help_needed", "Able to judge when additional help is needed"),
      item("c_attentive_near_roads", "Remains attentive near roads"),
      item("c_avoid_distraction", "Avoids unsafe distraction from mobile phones/headphones where relevant"),
      item("c_follow_crossing_strategy", "Follows an agreed safe crossing strategy"),
    ],
  },
  {
    id: "d", num: 4, title: "Public Transport & Travel Card", type: "transport",
    intro: "Section D — public transport skills, and Section E — Oyster/ticket/travel card skills where applicable.",
    transportItems: [
      item("d_correct_transport", "Recognises the correct bus/train/transport"),
      item("d_route_number", "Identifies the route number"),
      item("d_direction", "Identifies the correct direction"),
      item("d_correct_stop", "Identifies the correct stop/station"),
      item("d_waiting", "Waits safely"),
      item("d_boarding", "Boards safely"),
      item("d_passengers_exit", "Allows passengers to exit where appropriate"),
      item("d_safe_seat", "Finds a safe place to sit/stand"),
      item("d_travelling", "Travels appropriately"),
      item("d_monitoring", "Monitors journey progress"),
      item("d_landmarks", "Recognises landmarks"),
      item("d_destination_stop", "Identifies destination stop"),
      item("d_signal_stop", "Signals/requests stop where necessary"),
      item("d_exiting", "Exits safely"),
      item("d_moving_away", "Moves away from the vehicle safely"),
      item("d_onward_route", "Follows onward walking route"),
      item("d_interchanges", "Understands interchanges where applicable"),
    ],
    cardItems: [
      item("e_recognise_card", "Recognises their Oyster/travel card"),
      item("e_keep_secure", "Keeps the card secure"),
      item("e_present_tap", "Presents/taps the card correctly"),
      item("e_tap_in_out", "Understands when to tap in/out where relevant"),
      item("e_unsuccessful", "Recognises unsuccessful card use"),
      item("e_not_working", "Knows what to do if the card does not work"),
      item("e_card_lost", "Knows what to do if the card is lost"),
      item("e_staff_problem", "Communicates with transport staff if there is a problem"),
    ],
  },
  {
    id: "f", num: 5, title: "Personal Safety", type: "scale",
    intro: "Section F — stranger awareness & personal safety. Flag significant safeguarding concerns for authorised review.",
    items: [
      item("f_strangers", "Understanding of strangers"),
      item("f_appropriate_interaction", "Appropriate interaction with unfamiliar people"),
      item("f_not_leaving_with_unknown", "Does not leave the planned journey with an unknown person"),
      item("f_not_entering_vehicle", "Does not enter an unknown person's vehicle"),
      item("f_inappropriate_requests", "Recognises inappropriate requests"),
      item("f_boundaries", "Maintains appropriate personal boundaries"),
      item("f_protect_info", "Protects personal information"),
      item("f_appropriate_people", "Identifies appropriate people to approach for help (transport staff, police, school staff, agreed safe adults)"),
      item("f_uncomfortable", "Knows what to do if someone makes them uncomfortable"),
      item("f_followed", "Knows what to do if they believe they are being followed"),
      item("f_feel_unsafe", "Knows what to do if they feel unsafe"),
    ],
  },
  {
    id: "g", num: 6, title: "Emergency Skills", type: "scenarios",
    intro: "Section G — scenario-based problem solving. Record the child's likely response to realistic situations.",
    scenarios: [
      "The usual bus does not arrive",
      "The bus terminates before the normal destination",
      "The young person gets on the wrong bus",
      "The young person misses their stop",
      "The normal road is closed",
      "The bus/train is diverted",
      "Their Oyster/travel card does not work",
      "Their travel card is lost",
      "Their mobile phone battery dies",
      "They become lost",
      "They become separated from their trainer/support person",
      "A stranger offers them a lift",
      "Someone is behaving aggressively nearby",
      "They feel unwell during the journey",
      "There is an emergency on public transport",
    ],
  },
  {
    id: "functional", num: 7, title: "Functional Skills", type: "functional",
    intro: "Sections H–L — attention, memory & route learning; mobility & physical access; sensory & environmental factors; behaviour relevant to travel; digital skills.",
    routeItems: [
      item("h_multi_step", "Follows multi-step instructions"),
      item("h_landmarks", "Remembers key landmarks"),
      item("h_route_numbers", "Remembers bus/train numbers"),
      item("h_stop_names", "Remembers stop names"),
      item("h_familiar_locations", "Recognises familiar locations"),
      item("h_attention", "Maintains attention during the journey"),
      item("h_changes", "Responds to changes"),
      item("h_routine", "Follows an agreed routine"),
      item("h_safety_instructions", "Remembers safety instructions"),
      item("h_error", "Recognises when they have made an error"),
      item("h_seek_help", "Seeks assistance appropriately"),
    ],
    mobilityItems: [
      item("i_walking", "Walking appropriate journey distances"),
      item("i_steps", "Steps/stairs"),
      item("i_escalators", "Escalators where applicable"),
      item("i_lifts", "Lifts"),
      item("i_boarding", "Boarding/alighting transport"),
      item("i_balance", "Balance"),
      item("i_crowds", "Managing crowded environments"),
      item("i_mobility_aid", "Mobility aid requirements"),
      item("i_accessibility", "Accessibility requirements"),
      item("i_fatigue", "Fatigue considerations"),
      item("i_assistance", "Additional assistance required"),
    ],
    behaviourItems: [
      item("k_frustration", "Response to frustration"),
      item("k_delays", "Response to delays"),
      item("k_unexpected_change", "Response to unexpected change"),
      item("k_anxiety", "Anxiety during travel"),
      item("k_impulsive", "Impulsive actions affecting safety"),
      item("k_safety_instructions", "Ability to follow safety instructions"),
      item("k_safe_locations", "Ability to remain in agreed safe locations"),
      item("k_crowded", "Response to crowded environments"),
      item("k_seek_help_distress", "Ability to seek help when distressed"),
      item("k_helpful_strategies", "Known helpful strategies"),
    ],
    digitalItems: [
      item("l_carry_phone", "Carries a phone safely"),
      item("l_make_call", "Making a phone call"),
      item("l_answer_call", "Answering a call"),
      item("l_call_parent", "Calling parent/carer"),
      item("l_call_trainer", "Calling trainer"),
      item("l_emergency_contact", "Using an agreed emergency contact"),
      item("l_maps", "Using maps/journey applications if part of the training plan"),
      item("l_low_battery", "Recognising low battery"),
      item("l_keep_secure", "Keeping phone secure"),
      item("l_no_distraction", "Understanding not to become distracted by the phone near roads"),
      item("l_phone_lost", "Knowing what to do if the phone is lost/unavailable"),
    ],
  },
  {
    id: "experience", num: 8, title: "Experience & Views", type: "text",
    intro: "Sections M–O — existing travel experience, parent/carer views, and school/professional information.",
    sections: {
      m: {
        title: "Section M — Existing travel experience",
        fields: [
          ["m_current_method", "Current method of travelling to school"],
          ["m_school_transport", "School transport / parent transport / public transport with adult / walking with adult"],
          ["m_previous_independent", "Previous independent travel"],
          ["m_previous_training", "Previous travel training"],
          ["m_familiar_routes", "Familiar routes"],
          ["m_unfamiliar_routes", "Unfamiliar routes"],
          ["m_previous_incidents", "Previous incidents/concerns relevant to travel"],
          ["m_child_view", "What does the young person think about learning to travel independently?"],
        ],
      },
      n: {
        title: "Section N — Parent/carer views",
        fields: [
          ["n_view", "Parent/carer view of independent travel"],
          ["n_main_concerns", "Main concerns"],
          ["n_strengths", "Child's strengths"],
          ["n_experiences", "Previous travel experiences"],
          ["n_safety_concerns", "Known safety concerns"],
          ["n_strategies", "Strategies that work well"],
          ["n_emergency", "Emergency considerations"],
          ["n_desired_outcomes", "Desired outcomes"],
          ["n_provided_by", "Information provided by (name, relationship, date)"],
        ],
      },
      o: {
        title: "Section O — School / professional information",
        fields: [
          ["o_source", "Source (school, SENCO, local authority, social care, other)"],
          ["o_role", "Role"],
          ["o_date", "Date"],
          ["o_information", "Relevant information"],
          ["o_concerns", "Identified concerns"],
          ["o_strategies", "Recommended strategies"],
        ],
      },
    },
  },
  { id: "observed", num: 9, title: "Observed Journey", type: "observed",
    intro: "Optional but structured — record actual performance during an accompanied journey." },
  { id: "needs", num: 10, title: "Strengths, Needs & Adjustments", type: "needs" },
  { id: "summary", num: 11, title: "Summary & Recommendation", type: "summary" },
];

export const OBSERVED_JOURNEY_ITEMS = [
  item("oj_leaving_home", "Leaving home/safe starting point"),
  item("oj_walking", "Walking to transport"),
  item("oj_road_crossing", "Road crossing"),
  item("oj_locating_stop", "Locating stop/station"),
  item("oj_correct_transport", "Identifying correct transport"),
  item("oj_waiting", "Waiting safely"),
  item("oj_boarding", "Boarding"),
  item("oj_card_use", "Travel-card use"),
  item("oj_behaviour", "Behaviour during journey"),
  item("oj_route_recognition", "Route recognition"),
  item("oj_identifying_destination", "Identifying destination"),
  item("oj_exiting", "Exiting transport"),
  item("oj_walking_destination", "Walking to school/destination"),
  item("oj_emergency_awareness", "Emergency awareness"),
  item("oj_stranger_awareness", "Stranger awareness"),
  item("oj_support_required", "Overall level of support required"),
];

export const STRENGTH_OPTIONS = [
  "strong_route_memory", "good_communication", "motivation", "road_awareness",
  "confidence_using_buses", "ability_to_ask_for_help", "good_landmark_recognition",
];

export const TRAINING_NEED_AREAS = [
  "Road safety", "Route recognition", "Public transport", "Oyster/travel card", "Stranger awareness",
  "Emergency response", "Communication", "Problem solving", "Confidence",
  "Dealing with unexpected change", "Mobile phone skills", "Other",
];

export const ADJUSTMENT_OPTIONS = [
  "visual_journey_plan", "simplified_instructions", "repeated_route_practice", "photographic_route_prompts",
  "communication_aid", "quieter_travel_times", "additional_familiarisation", "gradual_reduction_of_support",
];

export const CONCERN_CATEGORIES = [
  "Road safety", "Stranger safety", "Communication", "Getting lost", "Public transport",
  "Health/emergency", "Behaviour relevant to safety", "Mobility", "Sensory", "Other",
];

export const TRANSPORT_TYPES = ["Bus", "Train", "London Underground", "Tram", "Other"];

export const COMMUNICATION_METHODS = ["Verbal", "Non-verbal", "Communication device", "Visual communication", "Other"];

export const SENSORY_TRIGGERS = [
  "Crowded buses/trains", "Loud noises", "Sirens", "Announcements", "Bright lighting", "Busy stations",
  "Unexpected physical contact", "Waiting", "Weather", "Delays", "Route changes", "Unfamiliar environments",
];
